import { writable, derived } from "svelte/store";
import type { User, Trip, Expense } from "./types";

// Auth Store
function createAuthStore() {
  const { subscribe, set, update } = writable<{
    user: User | null;
    isAuthenticated: boolean;
    loading: boolean;
  }>({
    user: null,
    isAuthenticated: false,
    loading: true,
  });

  return {
    subscribe,
    login: (user: User) => update((state) => ({ ...state, user, isAuthenticated: true, loading: false })),
    logout: () => set({ user: null, isAuthenticated: false, loading: false }),
    setLoading: (loading: boolean) => update((state) => ({ ...state, loading })),
  };
}

export const authStore = createAuthStore();

// Trips Store
function createTripsStore() {
  const { subscribe, set, update } = writable<{
    trips: Trip[];
    currentTrip: Trip | null;
    loading: boolean;
  }>({
    trips: [],
    currentTrip: null,
    loading: false,
  });

  return {
    subscribe,
    setTrips: (trips: Trip[]) => update((state) => ({ ...state, trips, loading: false })),
    setCurrentTrip: (trip: Trip | null) => update((state) => ({ ...state, currentTrip: trip })),
    addTrip: (trip: Trip) => update((state) => ({ ...state, trips: [trip, ...state.trips] })),
    updateTrip: (trip: Trip) =>
      update((state) => ({
        ...state,
        trips: state.trips.map((t) => (t.id === trip.id ? trip : t)),
        currentTrip: state.currentTrip?.id === trip.id ? trip : state.currentTrip,
      })),
    removeTrip: (id: string) =>
      update((state) => ({
        ...state,
        trips: state.trips.filter((t) => t.id !== id),
        currentTrip: state.currentTrip?.id === id ? null : state.currentTrip,
      })),
    setLoading: (loading: boolean) => update((state) => ({ ...state, loading })),
  };
}

export const tripsStore = createTripsStore();

// UI Store
export const uiStore = writable({
  sidebarOpen: true,
  theme: (typeof localStorage !== "undefined" ? localStorage.getItem("theme") : null) || "light" as "light" | "dark",
  notifications: [] as { id: string; type: "success" | "error" | "info"; message: string }[],
});

// Theme toggle — html.dark 클래스 + localStorage 연동
export function toggleTheme() {
  uiStore.update((state) => {
    const next = state.theme === "dark" ? "light" : "dark";
    applyTheme(next);
    return { ...state, theme: next };
  });
}

export function setTheme(theme: "light" | "dark") {
  applyTheme(theme);
  uiStore.update((state) => ({ ...state, theme }));
}

function applyTheme(theme: string) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
  localStorage.setItem("theme", theme);
}

// 초기 테마 적용 (SSR 안전)
if (typeof document !== "undefined") {
  const saved = localStorage.getItem("theme");
  // system preference fallback
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  const initial = saved || (prefersDark ? "dark" : "light");
  applyTheme(initial);
  if (!saved) {
    uiStore.update((state) => ({ ...state, theme: initial as "light" | "dark" }));
  }
}

export function addNotification(type: "success" | "error" | "info", message: string) {
  const id = crypto.randomUUID();
  uiStore.update((state) => ({
    ...state,
    notifications: [...state.notifications, { id, type, message }],
  }));

  // Auto remove after 5 seconds
  setTimeout(() => {
    uiStore.update((state) => ({
      ...state,
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  }, 5000);
}
